/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Overlay, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_TIMEPICKER_SCROLL_STRATEGY } from '@angular/material/timepicker';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EditorModule } from '@tinymce/tinymce-angular';
import { AppComponent } from 'app/core/app-component/app.component';
import { AppRoutesModule } from 'app/core/app-routes/app-routes.module';
import { ApiAuthService } from 'app/core/security/api/api-auth.service';
import { AuthService } from 'app/core/security/auth/auth.service';
import { AppAuthGuardService } from 'app/core/security/guards/app-auth-guard.service';
import { UserPermissionGuardService } from 'app/core/security/guards/user-permission-guard.service';
import { LoginComponent } from 'app/core/security/login/login.component';
import { LayoutModule } from 'app/layout/layout.module';
import { FilterFieldsBaseComponent } from 'app/shared/components/list-filter/filter-fields-base.component';
import { OrbitPaginatorIntl } from 'app/shared/services/orbit-paginator-intl';
import { environment } from 'environments/environment';
import { BlockUIModule } from 'ng-block-ui';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha';
import { ApiDependenciesService } from './api/services/api-dependencies.service';
import { AppMaterialCoreModule } from './core/app-material/app-material-core.module';
import { ForgotPasswordFormComponent } from './core/security/forgot-password/components/forgot-password-form.component';
import { ResetPasswordFormComponent } from './core/security/forgot-password/components/reset-password-form.component';
import { ForgotPasswordService } from './core/security/forgot-password/services/forgot-password-service';
import { OrganiserCreateFormComponent } from './core/security/organiser-onboarding/components/organiser-create-form.component';
import { OrganiserOnboardingService } from './core/security/organiser-onboarding/services/organiser-onboarding-service';
import { OtpComponent } from './core/security/otp/otp.component';
import { PermissionsService } from './core/security/permissions/permissions.service';
import { AsideService } from './layout/components/aside-container/aside.service';
import { UsersService } from './modules/configuration/users/user-management/services/users.service';
import { MsalWrapperModule } from './modules/msal-wrapper/msal-wrapper.module';
import { PrintLayoutComponent } from './print/print-layout.component';
import { PrintService } from './print/print.service';
import { NotificationHubService } from './shared/services/signalr.service';
import { SharedModule } from './shared/shared.module';

export const KALEIDA_UK_DATE_FORMATS = {
	parse: {
		dateInput: 'DD/MM/YYYY',
		timeInput: 'HH:mm',
	},
	display: {
		dateInput: 'DD/MM/YYYY',
		timeInput: 'HH:mm',
		timeOptionLabel: 'HH:mm',
		monthYearLabel: 'MM YYYY',
		dateA11yLabel: 'DD/MM/YYYY',
		monthYearA11yLabel: 'MM YYYY',
	},
};

//stops background scrolling when timepicker is open
//prevents the timepicker from scrolling away from the input when the page is scrolled
class OrbitTimepickerScrollStrategy implements ScrollStrategy {
	private readonly pageScrollStrategy: ScrollStrategy;
	private overlayRef: OverlayRef;
	private backdropElement: HTMLElement;
	private readonly preventBackgroundScroll = (event: Event): void => {
		const target = event.target;

		if (!(target instanceof Node) || !this.overlayRef?.overlayElement.contains(target)) {
			event.preventDefault();
		}
	};

	constructor(
		overlay: Overlay,
		private readonly document: Document,
	) {
		this.pageScrollStrategy = overlay.scrollStrategies.block();
	}

	attach(overlayRef: OverlayRef): void {
		this.overlayRef = overlayRef;
		this.pageScrollStrategy.attach(overlayRef);
	}

	enable(): void {
		this.addInteractionBackdrop();
		this.document.addEventListener('wheel', this.preventBackgroundScroll, { capture: true, passive: false });
		this.document.addEventListener('touchmove', this.preventBackgroundScroll, { capture: true, passive: false });
		this.pageScrollStrategy.enable();
	}

	disable(): void {
		this.pageScrollStrategy.disable();
		this.removeScrollLock();
	}

	detach(): void {
		this.pageScrollStrategy.detach?.();
		this.removeScrollLock();
	}

	private removeScrollLock(): void {
		this.document.removeEventListener('wheel', this.preventBackgroundScroll, true);
		this.document.removeEventListener('touchmove', this.preventBackgroundScroll, true);
		this.backdropElement?.remove();
		this.backdropElement = null;
	}

	private addInteractionBackdrop(): void {
		const overlayPane = this.overlayRef?.overlayElement;

		if (!overlayPane || this.backdropElement) {
			return;
		}

		let overlayHost = overlayPane;

		while (overlayHost.parentElement && !overlayHost.parentElement.classList.contains('cdk-overlay-container')) {
			overlayHost = overlayHost.parentElement;
		}

		const overlayContainer = overlayHost.parentElement;

		if (!overlayContainer) {
			return;
		}

		this.backdropElement = this.document.createElement('div');
		this.backdropElement.classList.add('cdk-overlay-backdrop', 'cdk-overlay-transparent-backdrop', 'cdk-overlay-backdrop-showing', 'orbit-timepicker-scroll-backdrop');
		overlayContainer.insertBefore(this.backdropElement, overlayHost);
	}
}

export function getOrbitTimepickerScrollStrategy(overlay: Overlay, document: Document): () => ScrollStrategy {
	return () => new OrbitTimepickerScrollStrategy(overlay, document);
}

@NgModule({
	declarations: [AppComponent, LoginComponent, OtpComponent, OrganiserCreateFormComponent, ForgotPasswordFormComponent, ResetPasswordFormComponent, FilterFieldsBaseComponent, PrintLayoutComponent],
	bootstrap: [AppComponent],
	imports: [
		BrowserModule,
		FormsModule,
		ReactiveFormsModule,
		BrowserAnimationsModule,
		AppMaterialCoreModule,
		MatCheckboxModule,
		//AppMaterialModule,
		BlockUIModule.forRoot(),
		SharedModule,
		// import 3rd party plugin imports after Angular imports
		LayoutModule,
		EditorModule,
		RecaptchaModule,
		RecaptchaFormsModule,
		//RecaptchaV3Module,
		MsalWrapperModule,
		AppRoutesModule, // This needs to be last.
	],
	providers: [
		AppAuthGuardService,
		AuthService,
		ApiAuthService,
		UserPermissionGuardService,
		ApiDependenciesService,
		PermissionsService,
		AsideService,
		PrintService,
		NotificationHubService,
		UsersService,
		OrganiserOnboardingService,
		ForgotPasswordService,
		{ provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
		{ provide: MAT_DATE_FORMATS, useValue: KALEIDA_UK_DATE_FORMATS },
		{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
		{ provide: MatPaginatorIntl, useClass: OrbitPaginatorIntl },
		{ provide: MAT_TIMEPICKER_SCROLL_STRATEGY, useFactory: getOrbitTimepickerScrollStrategy, deps: [Overlay, DOCUMENT] },
		{ provide: HTTP_INTERCEPTORS, useClass: AuthService, multi: true },
		{ provide: 'reCAPTCHA_site_key', useValue: environment.googleRecaptchaSiteKey },
		//{ provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.googleRecaptchaSiteKey },
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}
